import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import storage from '../storage';
import requiredDocuments from './requiredDocuments';

// Base API URL
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

/**
 * Get required documents for the employee
 * @returns {Promise<Object>} Response object with status and data
 */
const getRequiredDocuments = async () => {
  try {
    // Use requiredDocuments.getAllRequiredDocuments() to get documents based on company
    const response = await requiredDocuments.getAllRequiredDocuments();
    
    if (response.ok) {
      // Check if response.data is an array before using map
      if (Array.isArray(response.data)) {
        console.log('API returned required documents:', response.data);
        const formattedDocs = response.data.map(doc => ({
          ...doc,
          id: doc._id || doc.id,
          requiresExpiration: doc.requiresExpiration || false,
          allowedFileTypes: doc.allowedFileTypes || ['pdf', 'jpg', 'jpeg', 'png']
        }));
        console.log('Formatted documents for employee:', formattedDocs);
        return {
          ok: true,
          data: formattedDocs
        };
      } else {
        console.error('Expected array but got:', response.data);
        return {
          ok: false,
          error: 'Invalid document data format',
          data: []
        };
      }
    }
    return response;
  } catch (error) {
    console.error('Error fetching required documents:', error);
    return {
      ok: false,
      error: error.message || 'Failed to load required documents',
      data: []
    };
  }
};

/**
 * Get uploaded documents for the employee
 * @returns {Promise<Object>} Response object with status and data
 */
const getUploadedDocuments = async () => {
  try {
    // Get user data and token from storage
    const userDataString = await storage.getUser();
    const userData = userDataString ? JSON.parse(userDataString) : null;
    
    if (!userData || !userData._id) {
      throw new Error('User data or authentication token not found');
    }
    
    // Make API request to get uploaded documents
    const response = await axios.get(`${API_URL}/employee-documents/list`, {
      params: {
        employeeId: userData._id
      }
    });
    
    // Format response data into a map by document ID
    const uploadedDocsMap = {};
    response.data.forEach(doc => {
      uploadedDocsMap[doc.requiredDocumentId] = {
        ...doc,
        files: doc.files.map(file => ({
          name: file.name,
          size: file.size,
          type: file.type,
          dataUrl: file.url || file.dataUrl
        })),
        status: doc.status || 'pending_approval',
        uploadedAt: doc.createdAt || new Date().toISOString()
      };
    });
    
    return {
      ok: true,
      data: uploadedDocsMap
    };
  } catch (error) {
    console.error('Error fetching uploaded documents:', error);
    
    return {
      ok: false,
      error: error.response?.data?.message || 'Failed to load uploaded documents'
    };
  }
};

/**
 * Upload document files
 * @param {string} docId - The required document ID
 * @param {Object} uploadData - Object containing files and expirationDate
 * @returns {Promise<Object>} Response object with status
 */
const uploadDocument = async (docId, uploadData) => {
  try {
    // Get user data and token from storage
    const userDataString = await storage.getUser();
    const userData = userDataString ? JSON.parse(userDataString) : null;
    console.log("User data for upload:", userData);
    
    if (!userData || !userData._id) {
      throw new Error('User data or authentication token not found');
    }
    
    // Handle both old format (just files) and new format (object with files and expirationDate)
    let files, expirationDate;
    if (Array.isArray(uploadData)) {
      // Old format - just the files
      files = uploadData;
      expirationDate = null;
    } else {
      // New format - object with files and optional expirationDate
      files = uploadData.files;
      expirationDate = uploadData.expirationDate;
    }
    
    console.log(`Uploading ${files.length} files for document ID: ${docId}`, 
      expirationDate ? { expirationDate } : '');
    
    // Upload each file and collect results
    const uploadPromises = files.map(async (file) => {
      // Step 1: Get document upload URL
      const urlResponse = await axios.post(`${API_URL}/aws/document-url`, {
        file_type: file.type,
        documentType: docId,
        employeeId: userData._id
      });
      
      if (!urlResponse.data || !urlResponse.data.uploadURL) {
        throw new Error('Failed to get document upload URL');
      }
      
      const { uploadURL, documentFilename, documentKey } = urlResponse.data;
      console.log(`Got upload URL for file: ${file.name}`, { documentKey });
      
      // Step 2: Convert file to binary for upload
      const to_base64 = (file) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve(reader.result);
          reader.onerror = (error) => reject(error);
        });
      
      const blob = await to_base64(file);
      const binary = atob(blob.split(',')[1]);
      const array = [];
      
      for (let i = 0; i < binary.length; i++) {
        array.push(binary.charCodeAt(i));
      }
      
      const blobData = new Blob([new Uint8Array(array)], {
        type: file.type
      });
      
      // Step 3: Upload file directly to S3
      console.log(`Uploading file to S3: ${file.name}`);
      const uploadResult = await fetch(uploadURL, {
        method: 'PUT',
        body: blobData
      });
      
      if (!uploadResult.ok) {
        throw new Error(`Upload to S3 failed for file ${file.name}`);
      }
      
      // Step 4: Get permanent URL (removing query params)
      const permanentUrl = uploadURL.split('?')[0];
      console.log(`File uploaded successfully: ${file.name}`, { permanentUrl });
      
      // Return file info
      return {
        name: file.name,
        size: file.size,
        type: file.type,
        url: permanentUrl,
        key: documentKey
      };
    });
    
    // Wait for all uploads to complete
    const uploadedFiles = await Promise.all(uploadPromises);
    console.log(`Successfully uploaded ${uploadedFiles.length} files`);
    
    // Step 5: Save document reference in database
    console.log('Saving document reference to database', {
      employeeId: userData._id,
      requiredDocumentId: docId,
      files: uploadedFiles.length,
      expirationDate: expirationDate ? expirationDate.toISOString() : null
    });
    
    const response = await axios.post(`${API_URL}/employee-documents/save`, {
      employeeId: userData._id,
      requiredDocumentId: docId,
      files: uploadedFiles,
      status: 'pending_approval',
      expirationDate: expirationDate
    });
    
    console.log('Document reference saved successfully', response.data);
    
    return {
      ok: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error uploading document:', error);
  
    return {
      ok: false,
      error: error.response?.data?.message || 'Failed to upload document'
    };
  }
};

/**
 * Upload a single document file to AWS S3 and return the URL
 * @param {File} file - The file to upload
 * @param {string} documentType - Document type identifier (for organizing in S3)
 * @returns {Promise<Object>} Response object with status and URL data
 */
const uploadDocumentAndGetUrl = async (file, documentType) => {
  try {
    // Get user data for employee ID
    const userDataString = await storage.getUser();
    const userData = userDataString ? JSON.parse(userDataString) : null;
    
    if (!userData || !userData._id) {
      throw new Error('User data or authentication token not found');
    }
    
    // Step 1: Get document upload URL
    const urlResponse = await axios.post(`${API_URL}/aws/document-url`, {
      file_type: file.type,
      documentType: documentType || 'general',
      employeeId: userData._id
    });
    
    if (!urlResponse.data || !urlResponse.data.uploadURL) {
      throw new Error('Failed to get document upload URL');
    }
    
    const { uploadURL, documentFilename, documentKey } = urlResponse.data;
    
    // Step 2: Convert file to binary for upload
    const to_base64 = (file) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
      });
    
    const blob = await to_base64(file);
    const binary = atob(blob.split(',')[1]);
    const array = [];
    
    for (let i = 0; i < binary.length; i++) {
      array.push(binary.charCodeAt(i));
    }
    
    const blobData = new Blob([new Uint8Array(array)], {
      type: file.type
    });
    
    // Step 3: Upload file directly to S3
    const uploadResult = await fetch(uploadURL, {
      method: 'PUT',
      body: blobData
    });
    
    if (!uploadResult.ok) {
      throw new Error('Upload to S3 failed');
    }
    
    // Step 4: Get permanent URL (removing query params)
    const permanentUrl = uploadURL.split('?')[0];
    
    return {
      ok: true,
      data: {
        url: permanentUrl,
        key: documentKey,
        filename: documentFilename,
        name: file.name,
        size: file.size,
        type: file.type
      }
    };
  } catch (error) {
    console.error('Error uploading document to S3:', error);
    return {
      ok: false,
      error: error.message || 'Failed to upload document'
    };
  }
};

/**
 * Delete an uploaded document
 * @param {string} docId - The required document ID to delete
 * @returns {Promise<Object>} Response object with status
 */
const deleteDocument = async (docId) => {
  try {
    // Get user data and token from storage
    const userDataString = await storage.getUser();
    const userData = userDataString ? JSON.parse(userDataString) : null;
    
    if (!userData || !userData._id) {
      throw new Error('User data or authentication token not found');
    }
    
    // Make API request to delete document
    const response = await axios.delete(`${API_URL}/employee-documents/delete`, {
      params: {
        employeeId: userData._id,
        requiredDocumentId: docId
      }
    });
    
    return {
      ok: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error deleting document:', error);
    
    return {
      ok: false,
      error: error.response?.data?.message || 'Failed to delete document'
    };
  }
};

/**
 * Validate files against document requirements
 * @param {File[]} files - Array of files to validate
 * @param {Object} docRequirements - Document requirements object
 * @returns {string[]} Array of error messages (empty if valid)
 */
const validateFiles = (files, docRequirements) => {
  const errors = [];
  
  // Check if files are provided
  if (!files || files.length === 0) {
    errors.push('No files selected');
    return errors;
  }
  
  // Check number of files
  if (files.length > docRequirements.numberOfFiles) {
    errors.push(`Maximum ${docRequirements.numberOfFiles} file(s) allowed`);
  } else if (files.length < docRequirements.numberOfFiles) {
    errors.push(`Minimum ${docRequirements.numberOfFiles} file(s) required`);
  }
  
  // Check each file
  files.forEach(file => {
    // Check file type
    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (!docRequirements.allowedFileTypes.includes(fileExtension)) {
      errors.push(`File type .${fileExtension} not allowed for ${file.name}`);
    }
    
    // Check file size (MB)
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > docRequirements.maxFileSize) {
      errors.push(`File ${file.name} exceeds maximum size of ${docRequirements.maxFileSize}MB`);
    }
  });
  
  return errors;
};

// Export API functions
export default {
  getRequiredDocuments,
  uploadDocument,
  uploadDocumentAndGetUrl,
  validateFiles,
  getUploadedDocuments,
  deleteDocument
}; 